import type {
    ContentfulEntry,
    ContentfulExperimentFields,
    ContentfulExperimentHeroFields,
    ContentfulExperimentInfoFields,
    ContentfulExperimentsPageSettings,
    ContentfulSocialLinkFields
} from '@devtinker/shared';
import { Button, Layout } from '@devtinker/shared';
import { useEffect, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import ExperimentDetail from './components/ExperimentDetail';

function App() {
  const [socialLinks, setSocialLinks] = useState<ContentfulEntry<ContentfulSocialLinkFields>[]>([]);

  useEffect(() => {
    const fetchSocialLinks = async () => {
      const spaceId = import.meta.env.VITE_CONTENTFUL_SPACE_ID;
      const accessToken = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

      if (!spaceId || !accessToken) {
        return;
      }

      try {
        const response = await fetch(
          `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?content_type=socialLinks&access_token=${accessToken}`
        );
        const data = await response.json();
        
        if (data.items) {
          const links = data.items
            .filter((item: any) => item.fields.active !== false)
            .map((item: any) => ({
              sys: item.sys,
              fields: {
                platform: item.fields.platform,
                url: item.fields.url,
                icon: item.fields.icon,
                active: item.fields.active
              }
            }));
          setSocialLinks(links);
        }
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    };

    fetchSocialLinks();
  }, []);

  return (
    <Layout socialLinks={socialLinks}>
      <Routes>
        <Route path="/" element={<ExperimentsHome />} />
        <Route path="/:slug" element={<ExperimentDetail />} />
      </Routes>
    </Layout>
  );
}

function getExperimentStatusLabel(status: ContentfulExperimentFields['status']) {
  if (status === 'coming-soon') {
    return 'Coming Soon';
  }

  if (status === 'active') {
    return 'Active';
  }

  return 'Archived';
}

interface ExperimentsHomeProps {
  contentfulSpaceId?: string;
  contentfulAccessToken?: string;
}

export function ExperimentsHome({ contentfulSpaceId, contentfulAccessToken }: ExperimentsHomeProps = {}) {
  const [pageSettings, setPageSettings] = useState<ContentfulExperimentsPageSettings | null>(null);
  const [experiments, setExperiments] = useState<ContentfulEntry<ContentfulExperimentFields>[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      // Check if Contentful is configured (props take precedence over env vars)
      const spaceId = contentfulSpaceId || import.meta.env.VITE_CONTENTFUL_SPACE_ID;
      const accessToken = contentfulAccessToken || import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

      console.log('Contentful config:', { spaceId: spaceId ? '✓' : '✗', accessToken: accessToken ? '✓' : '✗' });

      if (!spaceId || !accessToken) {
        console.log('No Contentful credentials, using mock data');
        setIsLoading(false);
        return;
      }

      try {
        // Fetch experiments
        const experimentsResponse = await fetch(
          `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?access_token=${accessToken}&content_type=experiment&order=-fields.order`
        );

        if (experimentsResponse.ok) {
          const experimentsData = await experimentsResponse.json();
          console.log('Experiments response:', experimentsData);
          
          if (experimentsData.items && experimentsData.items.length > 0) {
            const experimentEntries: ContentfulEntry<ContentfulExperimentFields>[] = experimentsData.items.map((item: any) => ({
              sys: item.sys,
              fields: item.fields
            }));
            console.log('Loaded experiments:', experimentEntries);
            setExperiments(experimentEntries);
          }
        }

        // Try to fetch page settings (optional)
        try {
          const settingsResponse = await fetch(
            `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?access_token=${accessToken}&content_type=experimentsPageSettings&include=2`
          );

          if (settingsResponse.ok) {
            const data = await settingsResponse.json();
            console.log('Page settings response:', data);
            
            if (data.items && data.items.length > 0) {
              // Create a map of all included entries by ID for easy lookup
              const entriesMap = new Map();
              if (data.includes?.Entry) {
                data.includes.Entry.forEach((entry: any) => {
                  entriesMap.set(entry.sys.id, entry);
                });
              }

              // Helper function to resolve links
              const resolveLink = (link: any) => {
                if (!link?.sys?.id) return null;
                return entriesMap.get(link.sys.id) || null;
              };

              // Process the response and resolve all links
              const rawSettings = data.items[0].fields;
              const settings: ContentfulExperimentsPageSettings = {
                title: rawSettings.title,
                featuredExperimentsTitle: rawSettings.featuredExperimentsTitle,
                seoTitle: rawSettings.seoTitle,
                seoDescription: rawSettings.seoDescription,
                hero: resolveLink(rawSettings.hero),
                infoSection: resolveLink(rawSettings.infoSection),
                experiments: rawSettings.experiments?.map((link: any) => resolveLink(link)).filter(Boolean) || []
              };

              console.log('Resolved settings:', settings);
              setPageSettings(settings);
            }
          }
        } catch (settingsError) {
          console.log('No page settings found, using defaults');
        }
      } catch (error) {
        console.error('Error fetching Contentful data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Mock data as fallback
  const mockHero: ContentfulExperimentHeroFields = {
    title: 'Experiments Lab',
    description: 'A dedicated space for exploring experimental features, interactive demos, and cutting-edge web technologies.'
  };

  const mockExperiments: ContentfulEntry<ContentfulExperimentFields>[] = [
    {
      sys: {
        id: '9',
        type: 'Entry',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'Physics Sandbox',
        slug: 'physics-sandbox',
        description: 'A 2D interactive Verlet physics playground with collisions, rope constraints, gravity modes, and drag-to-fling controls.',
        technologies: ['React', 'TypeScript', 'Canvas', 'Verlet Integration', 'Physics Simulation'],
        status: 'active' as const,
        order: 7
      }
    },
    {
      sys: {
        id: '7',
        type: 'Entry',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'GLSL Shader Art',
        slug: 'glsl-art',
        description: 'A raw WebGL2 fragment shader gallery with preset procedural effects, live uniforms, and keyboard-driven shader switching.',
        technologies: ['React', 'TypeScript', 'WebGL', 'GLSL', 'Fragment Shaders'],
        status: 'active' as const,
        order: 6
      }
    },
    {
      sys: {
        id: '6',
        type: 'Entry',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'Fractal Explorer',
        slug: 'fractal-explorer',
        description: 'A dual-panel WebGL fractal viewer for exploring the Mandelbrot set and its paired Julia sets with zoom, pan, and palette controls.',
        technologies: ['React', 'TypeScript', 'WebGL', 'GLSL', 'Complex Numbers', 'Fractals'],
        status: 'active' as const,
        order: 8
      }
    },
    {
      sys: {
        id: '5',
        type: 'Entry',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'Flow Field',
        slug: 'flow-field',
        description: 'A noise-driven particle system where thousands of trails reveal a living vector field. Click the canvas to inject local turbulence and reshape the flow.',
        technologies: ['React', 'TypeScript', 'Canvas', 'Perlin Noise', 'Generative Art'],
        status: 'active' as const,
        order: 5
      }
    },
    {
      sys: {
        id: '4',
        type: 'Entry',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'Audio Visualizer',
        slug: 'audio-viz',
        description: 'Use the Web Audio API to inspect microphone, file, and oscillator signals in real time.',
        technologies: ['React', 'TypeScript', 'Canvas', 'Web Audio API', 'FFT'],
        status: 'active' as const,
        order: 4
      }
    },
    {
      sys: { 
        id: '1', 
        type: 'Entry', 
        createdAt: new Date().toISOString(), 
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'Canvas Animations',
        slug: 'canvas-animations',
        description: 'Explore HTML5 Canvas animations and interactive graphics.',
        technologies: ['HTML5 Canvas', 'JavaScript', 'Animation API'],
        status: 'coming-soon' as const,
        order: 1
      }
    },
    {
      sys: { 
        id: '2', 
        type: 'Entry', 
        createdAt: new Date().toISOString(), 
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'WebGL Demos',
        slug: 'webgl-demos',
        description: '3D graphics and WebGL-based interactive experiences.',
        technologies: ['WebGL', 'Three.js', 'GLSL'],
        status: 'coming-soon' as const,
        order: 2
      }
    },
    {
      sys: { 
        id: '3', 
        type: 'Entry', 
        createdAt: new Date().toISOString(), 
        updatedAt: new Date().toISOString(),
        contentType: { sys: { id: 'experiment' } }
      },
      fields: {
        title: 'Web APIs Showcase',
        slug: 'web-apis',
        description: 'A curated gallery of modern browser API demos including Gamepad, Wake Lock, Clipboard, Device Orientation, and more with live support detection.',
        technologies: ['React', 'TypeScript', 'Web APIs', 'PWA', 'Browser APIs'],
        status: 'active' as const,
        order: 9
      }
    }
  ];

  const mockInfo: ContentfulExperimentInfoFields = {
    title: 'What are Experiments?',
    content: 'This section is dedicated to testing new ideas, exploring emerging technologies, and showcasing interactive demonstrations. Each experiment represents a deep dive into a specific topic or technology, complete with code examples and explanations.',
    footerText: 'Check back regularly for new experiments and updates!'
  };

  // Use Contentful data or fallback to mock data
  const heroData = pageSettings?.hero?.fields || mockHero;
  const experimentsData = experiments.length > 0 ? experiments : (pageSettings?.experiments || mockExperiments);
  const infoData = pageSettings?.infoSection?.fields || mockInfo;
  const experimentsTitle = pageSettings?.featuredExperimentsTitle || 'Featured Experiments';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading experiments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center py-16 bg-gray-50 rounded-lg">
        <h2 className="text-5xl font-bold text-gray-900 mb-4">
          {heroData.title}
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          {heroData.description}
        </p>
      </section>

      {/* Experiments Grid */}
      <section>
        <h3 className="text-3xl font-bold text-gray-900 mb-8">{experimentsTitle}</h3>
        <div className="grid md:grid-cols-3 gap-8">
          {experimentsData.map((experiment) => {
            const statusLabel = getExperimentStatusLabel(experiment.fields.status);

            return (
              <ExperimentCard
                key={experiment.sys.id}
                title={experiment.fields.title}
                description={experiment.fields.description}
                status={statusLabel}
                technologies={experiment.fields.technologies}
                slug={experiment.fields.slug}
              />
            );
          })}
        </div>
      </section>

      {/* Info Section */}
      <section className="bg-blue-50 rounded-lg p-8 border border-blue-100">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {infoData.title}
          </h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            {infoData.content}
          </p>
          {infoData.footerText && (
            <p className="text-sm text-gray-600">
              {infoData.footerText}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

interface ExperimentCardProps {
  title: string;
  description: string;
  status: string;
  technologies?: string[];
  slug: string;
}

function ExperimentCard({ title, description, status, technologies, slug }: Readonly<ExperimentCardProps>) {
  const isActive = status === 'Active';
  
  return (
    <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200">
      <div className="mb-4">
        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
        <p className="text-gray-600 mb-4 leading-relaxed">{description}</p>
        {technologies && technologies.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {technologies.map((tech) => (
              <span key={tech} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {tech}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <span className={`inline-block text-sm px-3 py-1 rounded font-medium border ${
          isActive 
            ? 'bg-green-100 text-green-700 border-green-300' 
            : 'bg-gray-100 text-gray-700 border-gray-300'
        }`}>
          {status}
        </span>
        {isActive ? (
          <Link to={`/${slug}`}>
            <Button variant="outline" size="sm">
              View
            </Button>
          </Link>
        ) : (
          <Button variant="outline" size="sm" disabled>
            View
          </Button>
        )}
      </div>
    </div>
  );
}

export default App;
